import * as React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Check, X } from 'lucide-react';
export const Route = createFileRoute('/_layout/compare/linktree/')({
    component: CompareLinktreePage,
    head: () => ({
        meta: [
            { title: 'Adora vs Linktree — Campaign Analytics vs Link-in-Bio' },
            {
                name: 'description',
                content: 'Linktree groups links for social profiles. Adora measures offline campaign performance. See the full comparison and choose the right tool for your needs.',
            },
            { property: 'og:title', content: 'Adora vs Linktree — Campaign Analytics vs Link-in-Bio' },
            {
                property: 'og:description',
                content: 'Linktree groups links for social profiles. Adora measures offline campaign performance. See the full comparison and choose the right tool for your needs.',
            },
        ],
    }),
});
function CompareLinktreePage() {
    return (<main className="min-h-dvh bg-background">
            {/* Hero */}
            <section className="mx-auto max-w-6xl px-6 md:px-8 py-12 md:py-16">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Compare tools</p>

                <h1 className="mt-2 text-3xl md:text-5xl font-bold">Adora vs Linktree</h1>

                <p className="mt-4 max-w-2xl text-muted-foreground">
                    Both Adora and Linktree help people share links — but they are built for very different goals. This
                    page explains the differences so you can choose the right tool.
                </p>

                <div className="mt-6 flex flex-col sm:flex-row gap-3">
                    <Button asChild size="lg">
                        <Link to="/sign-up" search={{ redirect: '/console' }}>
                            Try Adora for free
                        </Link>
                    </Button>
                    <Button asChild variant="outline" size="lg">
                        <Link to="/what-is-adora">Learn what Adora is</Link>
                    </Button>
                </div>
            </section>

            <Separator />

            {/* Quick summary */}
            <section className="mx-auto max-w-6xl px-6 md:px-8 py-10">
                <div className="grid gap-6 md:grid-cols-2">
                    <Card>
                        <CardHeader>
                            <CardTitle>What Adora is best for</CardTitle>
                        </CardHeader>
                        <CardContent className="text-sm text-muted-foreground space-y-2">
                            <p>• Tracking real-world and offline campaigns</p>
                            <p>• QR codes on flyers, menus, packaging</p>
                            <p>• Understanding which placements actually work</p>
                            <p>• Agencies, printers, cafés, retail stores</p>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle>What Linktree is best for</CardTitle>
                        </CardHeader>
                        <CardContent className="text-sm text-muted-foreground space-y-2">
                            <p>• Bio link pages for creators</p>
                            <p>• Social media profile traffic</p>
                            <p>• Simple “link in bio” use cases</p>
                            <p>• Influencers and content creators</p>
                        </CardContent>
                    </Card>
                </div>
            </section>

            {/* Feature comparison */}
            <section className="mx-auto max-w-6xl px-6 md:px-8 py-10">
                <h2 className="text-2xl md:text-3xl font-semibold">Feature comparison</h2>

                <div className="mt-6 overflow-x-auto">
                    <table className="w-full border text-sm">
                        <thead className="bg-muted/50">
                            <tr>
                                <th className="px-4 py-3 text-left">Feature</th>
                                <th className="px-4 py-3 text-left">Adora</th>
                                <th className="px-4 py-3 text-left">Linktree</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y">
                            <Row label="QR codes for offline use" adora linktree={false}/>
                            <Row label="Campaign-based tracking" adora linktree={false}/>
                            <Row label="Offline → online analytics" adora linktree={false}/>
                            <Row label="A/B testing for links & placements" adora linktree={false}/>
                            <Row label="Printable QR & campaign assets" adora linktree={false}/>
                            <Row label="Simple bio link page" adora={false} linktree/>
                            <Row label="Creator-focused design" adora={false} linktree/>
                            <Row label="Social-first analytics" adora={false} linktree/>
                        </tbody>
                    </table>
                </div>
            </section>

            {/* When to choose which */}
            <section className="mx-auto max-w-6xl px-6 md:px-8 py-10">
                <h2 className="text-2xl md:text-3xl font-semibold">Which one should you choose?</h2>

                <div className="mt-6 grid gap-6 md:grid-cols-2">
                    <Card>
                        <CardHeader>
                            <CardTitle>Choose Adora if…</CardTitle>
                        </CardHeader>
                        <CardContent className="text-sm text-muted-foreground space-y-2">
                            <p>• You run physical campaigns (flyers, menus, posters)</p>
                            <p>• You care about which location or format performs best</p>
                            <p>• You want to measure real-world marketing ROI</p>
                            <p>• You work with clients or manage multiple campaigns</p>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle>Choose Linktree if…</CardTitle>
                        </CardHeader>
                        <CardContent className="text-sm text-muted-foreground space-y-2">
                            <p>• You are a creator or influencer</p>
                            <p>• Most traffic comes from social media bios</p>
                            <p>• You just need a simple link hub</p>
                            <p>• Offline tracking is not important</p>
                        </CardContent>
                    </Card>
                </div>
            </section>

            {/* CTA */}
            <section className="mx-auto max-w-6xl px-6 md:px-8 py-12">
                <Card>
                    <CardContent className="p-6 md:p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                        <div>
                            <h3 className="text-xl md:text-2xl font-semibold">
                                Built for real-world campaigns, not just bios
                            </h3>
                            <p className="mt-2 text-muted-foreground max-w-xl text-sm md:text-base">
                                If your marketing exists outside social media, Adora gives you the visibility Linktree
                                was never designed for.
                            </p>
                        </div>
                        <Button asChild size="lg">
                            <Link to="/sign-up" search={{ redirect: '/console' }}>
                                Start with Adora
                            </Link>
                        </Button>
                    </CardContent>
                </Card>
            </section>
        </main>);
}
const Row = ({ label, adora, linktree }) => (<tr>
        <td className="px-4 py-3">{label}</td>
        <td className="px-4 py-3">
            {adora ? <Check className="h-4 w-4 text-green-600"/> : <X className="h-4 w-4 text-muted-foreground"/>}
        </td>
        <td className="px-4 py-3">
            {linktree ? <Check className="h-4 w-4 text-green-600"/> : <X className="h-4 w-4 text-muted-foreground"/>}
        </td>
    </tr>);
